<template>
    <b-container>
        <b-row>
            <b-col cols-sm="12" cols-md="6" class="container-row">
                <div  class="authentication-card" :class="isMobile ? ' mobile-card': '' ">
                    <div class="center-content">
                        <h3 v-if="!isChangePassword">
                            Recover password
                        </h3>
                        <h3 v-else>
                          Change your password
                        </h3>
                    </div>
                    <b-form-group v-if="isChangePassword"
                        label="Old Password*"
                        label-for="old-password">
                        <b-form-input type="password"
                          id="old-password"
                          v-model="oldPassword"
                          placeholder="******"
                          :state="isValidInput(oldPassword)"
                        ></b-form-input>
                        <b-form-invalid-feedback>Please enter a password</b-form-invalid-feedback>
                    </b-form-group>
                    <b-form-group
                        label="Password*"
                        label-for="login-password">
                        <b-form-input type="password" id="login-password" v-model="newPassword" placeholder="******" :state="isValidPassword()"></b-form-input>
                        <b-form-invalid-feedback>Please enter a valid password</b-form-invalid-feedback>
                        <b-row class="mt-2">
                          <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneLowercase) }">
                            <span class="when-closed">
                              <b-icon icon="check" class="icon"></b-icon>
                            </span>
                            1 lowercase letter
                          </b-col>
                          <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneUppercase) }">
                            <span class="when-closed">
                              <b-icon icon="check" class="icon"></b-icon>
                            </span>
                            1 uppercase letter
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneDigit) }">
                            <span class="when-closed">
                              <b-icon icon="check" class="icon"></b-icon>
                            </span>
                            1 number
                          </b-col>
                          <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneSymbol) }">
                            <span class="when-closed">
                              <b-icon icon="check" class="icon"></b-icon>
                            </span>
                            1 symbol ($ % @...)
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.minEightChars) }">
                            <span class="when-closed">
                              <b-icon icon="check" class="icon"></b-icon>
                            </span>
                            At least 8 characters long
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col class="help-text" :class="{ disabled: (oldPassword === '' || oldPassword !== newPassword), 'text-danger': !(oldPassword === '' || oldPassword !== newPassword) }">
                            <span class="when-closed">
                              <b-icon icon="info-circle" class="icon"></b-icon>
                            </span>
                            New password cannot be the same as your old password
                          </b-col>
                        </b-row>
                    </b-form-group>
                    <b-form-group
                        label="Confirm password*"
                        label-for="login-password-confirmation">
                        <b-form-input type="password" id="login-password-confirmation" v-model="passwordConfirmation" placeholder="******" :state="isValidConfirmPassword()"></b-form-input>
                        <b-form-invalid-feedback>The passwords are different</b-form-invalid-feedback>
                    </b-form-group>
                    <div>
                        <b-button
                         @click="onUpdatePassword()"
                         variant="primary"
                         class="full-width"
                         :disabled="isLoading">
                         <template v-if="isLoading">
                            <loading loadingText="Submitting"></loading>
                          </template>
                          <template v-else>
                            Update password
                          </template>
                        </b-button>
                    </div>
                </div>
            </b-col>
        </b-row>
    </b-container>
</template>
<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import MxSecurity from '@/mixins/security'
import { IChangePasswordRequest, IUpdatePasswordWithCodeRequest } from '@/store/security/types'
import { TPIException } from '@/store/editor/types'
import Loading from '@/components/utility/loading.vue'
import { isAuthenticated } from '@/common/helpers/user-helpers'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'

const passwordValidators = {
  minEightChars: /^.{8,}$/,
  oneLowercase: /^(?=.*?[a-z])/,
  oneUppercase: /^(?=.*?[A-Z])/,
  oneDigit: /^(?=.*?[0-9])/,
  oneSymbol: /^(?=.*?[" !"#$%&'()+,-.*/:;<=>?@[\]^_`{|}~"])/,
}

@Component({
  components: {
    Loading,
  },
})

export default class extends Mixins(MxSecurity) {
  public isMobile = isMobile()
  public validators = passwordValidators
  public oldPassword = ''
  public newPassword = ''
  public passwordConfirmation = ''
  public didSubmit = false
  public isLoading = false

  public created() {
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
  }

  public onUpdatePassword() {
    const { newPassword } = this

    this.didSubmit = true
    this.isLoading = true

    if (!this.isFormValid) {
      this.isLoading = false
      return
    }

    if (this.isChangePassword) {
      const changePasswordRequest : IChangePasswordRequest = {
        UserName: this.resetUsername || this.currentUser.Username,
        OldPassword: this.oldPassword,
        NewPassword: newPassword,
      }

      const { $router } = this

      this.changePassword({ changePasswordRequest }).then(() => {
        // TODO JM: redirect user to previous page
        this.toastSuccess('Password changed successfully')
        if (isAuthenticated(this.currentUser)) {
          $router.push('/')
        } else {
          $router.push('/authentication/login')
        }
      }).catch(({ Message } : TPIException) => {
        this.toastError(Message)
      }).finally(() => {
        this.isLoading = false
      })
    } else {
      // TODO jm: validate the query params
      const resetPasswordRequest : IUpdatePasswordWithCodeRequest = {
        Code: this.$route.query.code.toString(),
        Id: this.$route.query.id.toString(),
        NewPassword: newPassword,
      }

      const { $router } = this

      this.updatepassword({ resetPasswordRequest }).then(() => {
        // TODO JM: redirect user to previous page
        $router.push('/authentication/login')
      }).catch(({ Message } : TPIException) => {
        this.toastError(Message)
      }).finally(() => {
        this.isLoading = false
      })
    }
  }

  public isValidPassword() {
    if (!this.didSubmit) {
      return null
    }

    return this.validatePasswordRegex(this.validators.minEightChars) &&
        this.validatePasswordRegex(this.validators.oneDigit) &&
        this.validatePasswordRegex(this.validators.oneLowercase) &&
        this.validatePasswordRegex(this.validators.oneUppercase) &&
        this.validatePasswordRegex(this.validators.oneSymbol) &&
        (this.oldPassword === '' || this.oldPassword !== this.newPassword)
  }

  public validatePasswordRegex(regex: any): boolean {
    return regex.test(this.newPassword)
  }

  public isValidConfirmPassword() {
    return !this.didSubmit || this.newPassword === ''
      ? null
      : this.passwordConfirmation === this.newPassword
  }

  private get isFormValid(): boolean {
    return (this.isValidPassword() ?? false) &&
      (this.isValidConfirmPassword() ?? false)
  }

  public isValidInput(value: string) : boolean | null {
    return !this.didSubmit
      ? null
      : (value ?? '').length > 0
  }

  get isChangePassword(): boolean {
    return this.passwordExpired || this.$route.params.change !== undefined
  }
}
</script>
<style>
    @import '../../styles/authentication.css'
</style>
